<template>
  <div>
    <Echart
      ref="chartRef"
      :options="options"
      id="bottomLeftChart"
      height="440px"
      width="100%"
    />
  </div>
</template>

<script>
import Echart from '@/common/echart'

export default {
  components: {
    Echart
  },
  props: {
    cdata: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      options: {},
      scrollTimer: null,
      scrollIndex: 0,
      visibleCount: 12,
      projectCount: 0
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.bindChartEvents()
      this.startAutoScroll()
    })
  },
  beforeDestroy() {
    this.stopAutoScroll()
  },
  watch: {
    cdata: {
      handler(newData) {
        const projects = newData.projects || []
        const projectNames = projects.map(item => item.name)
        const totals = projects.map(item => (
          item.labor + item.professional + item.material + item.consulting
        ))
        const contractTotal = totals.reduce((sum, value) => sum + value, 0)
        const zoomEnd = Math.min(100, (this.visibleCount / projects.length) * 100)
        this.projectCount = projects.length

        const gradients = {
          labor: new this.$echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: '#10d7c4' },
            { offset: 1, color: '#35a7ff' }
          ]),
          professional: new this.$echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: '#ff5b88' },
            { offset: 1, color: '#ff9564' }
          ]),
          material: new this.$echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: '#3978ff' },
            { offset: 1, color: '#8a6cff' }
          ]),
          consulting: new this.$echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: '#ffd34e' },
            { offset: 1, color: '#ff8d35' }
          ])
        }
        const contractKeys = ['labor', 'professional', 'material', 'consulting']
        const barData = (key, color) => projects.map(item => {
          const activeKeys = contractKeys.filter(contractKey => item[contractKey] > 0)
          const isFirst = activeKeys[0] === key
          const isLast = activeKeys[activeKeys.length - 1] === key
          return {
            value: item[key],
            itemStyle: {
              color,
              barBorderRadius: [isFirst ? 8 : 0, isLast ? 8 : 0, isLast ? 8 : 0, isFirst ? 8 : 0],
              shadowBlur: item[key] ? 8 : 0,
              shadowColor: 'rgba(50, 197, 233, 0.28)'
            }
          }
        })

        this.options = {
          animationDuration: 700,
          animationDurationUpdate: 650,
          animationEasingUpdate: 'cubicOut',
          title: {
            top: 0,
            right: 48,
            text: `{number|${projects.length}} {label|个项目}   {number|${contractTotal}} {label|份合同}`,
            textStyle: {
              rich: {
                number: {
                  color: '#62f4df',
                  fontSize: 16,
                  fontWeight: 'bold'
                },
                label: {
                  color: '#8795b5',
                  fontSize: 11,
                  fontWeight: 'normal'
                }
              }
            }
          },
          legend: {
            top: 4,
            left: 245,
            itemWidth: 14,
            itemHeight: 7,
            itemGap: 14,
            data: ['劳务合同', '专业分包', '材料采购', '设计/咨询'],
            textStyle: {
              color: '#c3cbde',
              fontSize: 12
            }
          },
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(8, 16, 36, 0.96)',
            borderColor: '#2fc8dc',
            borderWidth: 1,
            padding: [10, 12],
            textStyle: {
              color: '#dce8f8',
              fontSize: 12
            },
            axisPointer: {
              type: 'shadow',
              shadowStyle: {
                color: 'rgba(68, 184, 255, 0.08)'
              }
            },
            formatter(params) {
              const visibleSeries = params.filter(item => item.seriesName !== '合同合计')
              const total = visibleSeries.reduce((sum, item) => sum + Number(item.value), 0)
              const rows = visibleSeries.map(item => (
                `${item.marker}${item.seriesName}：${item.value} 份`
              ))
              return [
                `<div style="max-width:360px;margin-bottom:6px;color:#fff;font-weight:bold;white-space:normal;">${params[0].axisValue}</div>`,
                ...rows,
                `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,.15);color:#62f4df;">合同合计：${total} 份</div>`
              ].join('<br/>')
            }
          },
          grid: {
            top: 42,
            right: 58,
            bottom: 18,
            left: 245,
            containLabel: false
          },
          xAxis: {
            type: 'value',
            minInterval: 1,
            name: '份',
            nameTextStyle: {
              color: '#8e9ab5'
            },
            axisLine: {
              lineStyle: {
                color: '#3d4660'
              }
            },
            axisLabel: {
              color: '#7887a6',
              fontSize: 10
            },
            splitLine: {
              lineStyle: {
                color: 'rgba(102, 175, 220, 0.09)',
                type: 'dashed'
              }
            }
          },
          yAxis: {
            type: 'category',
            inverse: true,
            data: projectNames,
            axisTick: {
              show: false
            },
            axisLine: {
              lineStyle: {
                color: '#3d4660'
              }
            },
            axisLabel: {
              color: '#c3cbde',
              fontSize: 11,
              width: 220,
              overflow: 'truncate',
              formatter(value) {
                return value.length > 18 ? `${value.slice(0, 18)}...` : value
              }
            },
            splitArea: {
              show: true,
              areaStyle: {
                color: ['rgba(45, 115, 160, 0.035)', 'rgba(255, 255, 255, 0)']
              }
            }
          },
          dataZoom: [
            {
              type: 'inside',
              yAxisIndex: 0,
              start: 0,
              end: zoomEnd,
              zoomOnMouseWheel: false,
              moveOnMouseWheel: true,
              moveOnMouseMove: true
            },
            {
              type: 'slider',
              yAxisIndex: 0,
              start: 0,
              end: zoomEnd,
              right: 12,
              width: 8,
              showDetail: false,
              showDataShadow: false,
              borderColor: 'transparent',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              fillerColor: 'rgba(45, 220, 218, 0.45)',
              handleSize: 0
            }
          ],
          series: [
            {
              name: '劳务合同',
              type: 'bar',
              stack: 'contracts',
              barWidth: 16,
              data: barData('labor', gradients.labor)
            },
            {
              name: '专业分包',
              type: 'bar',
              stack: 'contracts',
              barWidth: 16,
              data: barData('professional', gradients.professional)
            },
            {
              name: '材料采购',
              type: 'bar',
              stack: 'contracts',
              barWidth: 16,
              data: barData('material', gradients.material)
            },
            {
              name: '设计/咨询',
              type: 'bar',
              stack: 'contracts',
              barWidth: 16,
              data: barData('consulting', gradients.consulting)
            },
            {
              name: '合同合计',
              type: 'bar',
              barGap: '-100%',
              barWidth: 16,
              silent: true,
              data: totals,
              itemStyle: {
                color: 'transparent'
              },
              label: {
                show: true,
                position: 'right',
                color: '#edf7ff',
                fontSize: 11,
                fontWeight: 'bold',
                formatter: '{c}'
              }
            }
          ]
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    bindChartEvents() {
      const chart = this.$refs.chartRef && this.$refs.chartRef.chart
      if (!chart) return

      chart.on('mouseover', this.stopAutoScroll)
      chart.on('globalout', this.startAutoScroll)
    },
    startAutoScroll() {
      this.stopAutoScroll()
      if (this.projectCount <= this.visibleCount) return

      this.scrollTimer = setInterval(() => {
        const chart = this.$refs.chartRef && this.$refs.chartRef.chart
        if (!chart) return

        const maxStart = this.projectCount - this.visibleCount
        this.scrollIndex = this.scrollIndex >= maxStart ? 0 : this.scrollIndex + 1
        const start = (this.scrollIndex / this.projectCount) * 100
        const end = ((this.scrollIndex + this.visibleCount) / this.projectCount) * 100
        chart.dispatchAction({
          type: 'dataZoom',
          start,
          end
        })
      }, 1800)
    },
    stopAutoScroll() {
      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
        this.scrollTimer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
